// Get API URL from environment variable (set in .env file)
const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:3000/api/v1";

export interface ApiError {
  message: string;
  status: number;
  errors?: Array<{ field: string; message: string }>;
}

export class ApiService {
  private baseUrl: string;
  private isRefreshing: boolean = false;
  private refreshPromise: Promise<string | null> | null = null;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  /**
   * Attempts to refresh the access token
   */
  private async refreshToken(): Promise<string | null> {
    // If already refreshing, return the existing promise
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    this.isRefreshing = true;
    this.refreshPromise = (async () => {
      try {
        const token = localStorage.getItem("admin_token");
        if (!token) {
          return null;
        }

        const response = await fetch(`${this.baseUrl}/admin/auth/refresh`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          // Refresh failed, clear auth data
          localStorage.removeItem("admin_token");
          localStorage.removeItem("admin_data");
          return null;
        }

        const data = await response.json();
        localStorage.setItem("admin_token", data.accessToken);
        localStorage.setItem("admin_data", JSON.stringify(data.admin));
        return data.accessToken;
      } catch (error) {
        // Refresh failed, clear auth data
        localStorage.removeItem("admin_token");
        localStorage.removeItem("admin_data");
        return null;
      } finally {
        this.isRefreshing = false;
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  /**
   * Redirects to login page
   */
  private redirectToLogin(): void {
    // Only redirect if we're not already on the login page
    if (window.location.pathname !== "/admin/login") {
      localStorage.removeItem("admin_token");
      localStorage.removeItem("admin_data");
      window.location.href = "/admin/login";
    }
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    retryCount: number = 0
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    
    // Skip auth for login and refresh endpoints
    const isAuthEndpoint = endpoint.includes("/admin/auth/login") || endpoint.includes("/admin/auth/refresh");
    
    // Get JWT token from localStorage if available
    const token = localStorage.getItem("admin_token");
    
    // Build headers object
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    
    // Merge existing headers if provided
    if (options.headers) {
      if (options.headers instanceof Headers) {
        options.headers.forEach((value, key) => {
          headers[key] = value;
        });
      } else if (Array.isArray(options.headers)) {
        options.headers.forEach(([key, value]) => {
          headers[key] = value;
        });
      } else {
        Object.assign(headers, options.headers);
      }
    }
    
    // Add Authorization header if token exists and not auth endpoint
    if (token && !isAuthEndpoint) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    
    const config: RequestInit = {
      ...options,
      headers,
    };

    try {
      const response = await fetch(url, config);

      // Check if response is JSON
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const text = await response.text();
        const error = new Error(
          `Expected JSON but got ${contentType}. Response: ${text.substring(0, 200)}`
        ) as Error & ApiError;
        error.status = response.status;
        throw error;
      }

      if (!response.ok) {
        // Handle 401 Unauthorized
        if (response.status === 401 && !isAuthEndpoint && retryCount === 0) {
          // Try to refresh token
          const newToken = await this.refreshToken();
          
          if (newToken) {
            // Retry the request with new token
            return this.request<T>(endpoint, options, retryCount + 1);
          } else {
            // Refresh failed, redirect to login
            this.redirectToLogin();
            const error = new Error(
              "تم انتهاء صلاحية الجلسة. يرجى تسجيل الدخول مرة أخرى"
            ) as Error & ApiError;
            error.status = 401;
            throw error;
          }
        }

        const errorData = await response.json().catch(() => ({}));
        const error: ApiError = {
          message:
            errorData.message || errorData.error || `HTTP error! status: ${response.status}`,
          status: response.status,
          errors: errorData.errors || (errorData.message ? [{ message: errorData.message }] : []),
        };
        
        // If still 401 after refresh attempt, redirect to login
        if (response.status === 401 && !isAuthEndpoint) {
          this.redirectToLogin();
        }
        
        throw error;
      }

      return await response.json();
    } catch (error) {
      if (error instanceof Error && "status" in error) {
        throw error;
      }
      // Handle network/connection errors
      const errorMessage = error instanceof Error ? error.message : "Network error";
      const isConnectionError =
        errorMessage.includes("Failed to fetch") ||
        errorMessage.includes("NetworkError") ||
        errorMessage.includes("CONNECTION_REFUSED") ||
        errorMessage.includes("ERR_CONNECTION_REFUSED");

      const networkError = new Error(
        isConnectionError
          ? "CONNECTION_REFUSED: لا يمكن الاتصال بالخادم"
          : errorMessage
      ) as Error & ApiError;
      networkError.status = 0;
      throw networkError;
    }
  }

  async get<T>(endpoint: string, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: "GET" });
  }

  async post<T>(
    endpoint: string,
    data?: any,
    options?: RequestInit
  ): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: "POST",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T>(
    endpoint: string,
    data?: any,
    options?: RequestInit
  ): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: "PUT",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async patch<T>(
    endpoint: string,
    data?: any,
    options?: RequestInit
  ): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: "PATCH",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(endpoint: string, options?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: "DELETE" });
  }
}

export const apiService = new ApiService();
